import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

function randomState(len = 16) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export default function Connect() {
  const [searchParams]  = useSearchParams();
  const isReconnect     = searchParams.get('reconnect') === 'true';
  const [connecting, setConnecting] = useState(false);

  function handleConnect() {
    setConnecting(true);
    const clientId    = process.env.REACT_APP_QB_CLIENT_ID || '';
    const redirectUri = process.env.REACT_APP_QB_REDIRECT_URI || 'https://app.acct4cast.com/auth/qb/callback';
    const state       = randomState();

    const params = new URLSearchParams({
      client_id:     clientId,
      response_type: 'code',
      scope:         'com.intuit.quickbooks.accounting',
      redirect_uri:  redirectUri,
      state,
    });

    window.location.href = `https://appcenter.intuit.com/connect/oauth2?${params.toString()}`;
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Logo row */}
        <div style={styles.logoRow}>
          <span style={styles.qbBadge}>QB</span>
          <span style={styles.arrow}>→</span>
          <span style={styles.wordmark}>
            <span style={{ color: '#1A7FBE' }}>Acct</span>
            <span style={{ color: '#C8A000' }}>4</span>
            <span style={{ color: '#3DAA4A' }}>Cast</span>
          </span>
        </div>

        {/* Eyebrow */}
        <p style={styles.eyebrow}>QuickBooks Integration</p>

        {/* Heading */}
        <h1 style={styles.heading}>
          {isReconnect ? 'Reconnect QuickBooks' : 'Connect QuickBooks'}
        </h1>

        {/* Body text */}
        <p style={styles.body}>
          {isReconnect
            ? 'Your QuickBooks connection has expired. Reconnect to resume live data sync, forecasts, variance alerts, and automated reports.'
            : 'Connect your QuickBooks Online account to activate your Acct4Cast dashboard. Read-only access — your books are never modified.'}
        </p>

        {/* Feature list */}
        <ul style={styles.featureList}>
          {[
            'Read-only access — your data is never modified',
            'Works with all QuickBooks Online plans',
            'Reconnects in under 60 seconds',
          ].map((item) => (
            <li key={item} style={styles.featureItem}>
              <span style={styles.check}>✓</span>
              {item}
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <button
          onClick={handleConnect}
          disabled={connecting}
          style={{
            ...styles.cta,
            opacity: connecting ? 0.7 : 1,
            cursor:  connecting ? 'not-allowed' : 'pointer',
          }}
        >
          {connecting
            ? 'Connecting...'
            : isReconnect
              ? 'Reconnect to QuickBooks →'
              : 'Connect to QuickBooks →'}
        </button>

        {/* Fine print */}
        <p style={styles.finePrint}>Secure OAuth 2.0 · Read-only · Intuit verified</p>
      </div>

      {/* Back link */}
      <a href="https://acct4cast.com" style={styles.backLink}>← Back to acct4cast.com</a>
    </div>
  );
}

const styles = {
  page: {
    minHeight:       '100vh',
    backgroundColor: '#0C1A36',
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    justifyContent:  'center',
    padding:         '2rem 1rem',
    animation:       'fadeUp 0.5s ease both',
  },
  card: {
    width:           '100%',
    maxWidth:        '440px',
    backgroundColor: 'rgba(17,34,64,0.9)',
    border:          '1px solid rgba(0,168,204,0.25)',
    borderRadius:    '16px',
    padding:         '2.5rem',
    boxShadow:       '0 8px 40px rgba(0,0,0,0.4)',
  },
  logoRow: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            '0.75rem',
    marginBottom:   '1.5rem',
  },
  qbBadge: {
    backgroundColor: '#2CA01C',
    color:           '#fff',
    fontFamily:      "'Sora', sans-serif",
    fontWeight:      700,
    fontSize:        '0.85rem',
    padding:         '0.3rem 0.6rem',
    borderRadius:    '6px',
    letterSpacing:   '0.05em',
  },
  arrow: {
    color:      '#C8A000',
    fontSize:   '1.25rem',
    fontWeight: 700,
  },
  wordmark: {
    fontFamily: "'Sora', sans-serif",
    fontWeight: 800,
    fontSize:   '1.25rem',
  },
  eyebrow: {
    fontFamily:    "'DM Mono', monospace",
    color:         '#00A8CC',
    fontSize:      '0.72rem',
    fontWeight:    500,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    textAlign:     'center',
    marginBottom:  '0.6rem',
    margin:        '0 0 0.6rem',
  },
  heading: {
    fontFamily:   "'Sora', sans-serif",
    fontWeight:   800,
    fontSize:     '1.75rem',
    color:        '#EDF1FF',
    textAlign:    'center',
    margin:       '0 0 1rem',
    lineHeight:   1.2,
  },
  body: {
    fontFamily:  "'Sora', sans-serif",
    color:       '#7A90B8',
    fontSize:    '0.9rem',
    lineHeight:  1.65,
    textAlign:   'center',
    margin:      '0 0 1.25rem',
  },
  featureList: {
    listStyle:   'none',
    padding:     0,
    margin:      '0 0 1.5rem',
  },
  featureItem: {
    fontFamily:  "'Sora', sans-serif",
    color:       '#7A90B8',
    fontSize:    '0.84rem',
    display:     'flex',
    alignItems:  'center',
    gap:         '0.5rem',
    marginBottom:'0.45rem',
  },
  check: {
    color:      '#3DAA4A',
    fontWeight: 700,
    flexShrink: 0,
  },
  cta: {
    width:        '100%',
    padding:      '0.85rem 1.5rem',
    background:   'linear-gradient(135deg, #00A8CC 0%, #3DAA4A 100%)',
    border:       'none',
    borderRadius: '10px',
    color:        '#fff',
    fontFamily:   "'Sora', sans-serif",
    fontWeight:   700,
    fontSize:     '1rem',
    letterSpacing:'0.02em',
    marginBottom: '1rem',
    transition:   'opacity 0.2s',
  },
  finePrint: {
    fontFamily:   "'DM Mono', monospace",
    color:        '#7A90B8',
    fontSize:     '0.7rem',
    textAlign:    'center',
    margin:       0,
  },
  backLink: {
    marginTop:  '1.25rem',
    color:      '#7A90B8',
    fontSize:   '0.75rem',
    fontFamily: "'Sora', sans-serif",
    textDecoration: 'none',
  },
};
